import React, { useEffect } from "react";
import { CloseOutlined, ArrowBack } from "@mui/icons-material";
import type { GenericModalProps } from "./types";
import InfoTooltip from "../InfoTooltip/InfoTooltip";

const Modal: React.FC<GenericModalProps> = ({
	isOpen,
	onClose,
	title,
	icon,
	showCloseIcon = true,
	children,
	footer,
	width = "max-w-md",
	height = "auto",
	backgroundColor = "bg-white",
	rounded = "rounded-2xl",
	padding = "p-6",
	showBackIcon,
	onBack,
	className = "",
	isTooltipEnabled,
	tooltipText,
}) => {
	// Determine responsive width based on content
	const getResponsiveWidth = () => {
		// For smaller content like filters, use smaller width
		if (width === "max-w-md" || width === "max-w-sm" || width === "max-w-lg") {
			return width;
		}
		// For larger content like committee details, use larger width
		if (width === "max-w-4xl" || width === "max-w-5xl" || width === "max-w-6xl") {
			return width;
		}
		// Default responsive behavior
		return width;
	};

	const responsiveWidth = getResponsiveWidth();
	// Prevent body scroll when modal is open
	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "unset";
		}

		return () => {
			document.body.style.overflow = "unset";
		};
	}, [isOpen]);

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30" role="dialog">
			<div
				className={`w-full ${responsiveWidth} ${height} ${backgroundColor} ${rounded} ${padding} relative shadow-xl ${className}`}>
				{/* Header Row */}
				{(title || icon || showCloseIcon) && (
					<div className="flex justify-between items-start mb-4">
						<div className="flex items-center gap-2">
							{showBackIcon && (
								<button
									onClick={onBack}
									className="text-gray-600 hover:text-gray-900 transition-colors p-1 -ml-1 rounded-full bg-white">
									<ArrowBack className="w-5 h-5" />
								</button>
							)}
							{icon && <div className="text-xl text-primary mb-1">{icon}</div>}
							{title && <div className="text-2xl font-semibold text-gray-900">{title}</div>}
							{isTooltipEnabled && <InfoTooltip tooltip={tooltipText} />}
						</div>
						{showCloseIcon && (
							<button onClick={onClose} className="text-gray-400 hover:text-black">
								<CloseOutlined />
							</button>
						)}
					</div>
				)}

				{/* Modal Body */}
				<div>{children}</div>

				{/* Footer */}
				{footer && <div className="mt-6">{footer}</div>}
			</div>
		</div>
	);
};

export default Modal;
